import { useEffect, useRef } from 'react';

import {
  ensureCssLoaded,
  fixResponsiveDisplayClasses,
  setupEventDelegation,
  useMirroredLeftNav,
} from './useMirroredLeftNav';

interface MirroredLeftNavProps {
  /**
   * - `desktop`: makes the desktop sidebar visible (fixes Semantic UI / Tailwind
   *   conflict) and keeps the mobile overlay hidden.
   * - `mobile`: keeps the desktop sidebar hidden and leaves the mobile overlay
   *   in its initial hidden state, ready to be toggled by a hamburger button.
   */
  variant: 'desktop' | 'mobile';
}

/**
 * Presentational component that injects the mirrored left nav HTML from Nova
 * into the DOM. Reads the shared HTML from the `useMirroredLeftNav` context
 * (provided by `MirroredLeftNavProvider`).
 *
 * Both the desktop and mobile layout containers render their own instance of
 * this component with the appropriate variant. The switcher modal id is shared
 * by both injected copies, so DOM queries must stay scoped to the container.
 */
const MirroredLeftNav: React.FC<MirroredLeftNavProps> = ({ variant }) => {
  const { navHtml, cssHrefs } = useMirroredLeftNav();
  const containerRef = useRef<HTMLDivElement>(null);
  const injectedRef = useRef(false);
  const stopDelegationRef = useRef<() => void>();

  useEffect(() => () => stopDelegationRef.current?.(), []);

  useEffect(() => {
    if (!navHtml || !containerRef.current || injectedRef.current) return;
    injectedRef.current = true;

    const container = containerRef.current;

    // Parse the shared HTML so each variant can apply its own transforms
    // without mutating the original string.
    const tmp = document.createElement('div');
    tmp.innerHTML = navHtml;
    const root = tmp.firstElementChild as HTMLElement;

    if (variant === 'desktop') {
      // Make the desktop sidebar visible. Semantic UI's
      // `.hidden { display: none !important }` clobbers Tailwind's `lg:block`,
      // so we replace those class pairs with inline styles.
      fixResponsiveDisplayClasses(root);
    }
    // For `mobile`, no processing needed — the desktop sidebar stays hidden
    // via the `.hidden` class, and #left-nav-mobile starts hidden until the
    // hamburger button toggles it.

    container.innerHTML = root.outerHTML;
    stopDelegationRef.current = setupEventDelegation(container);

    // Wait for Nova's CSS to load before revealing the nav. On the first
    // load the Provider already waited for CSS, so ensureCssLoaded resolves
    // immediately. On cache-hit renders the browser serves CSS from its
    // HTTP cache (near-instant) but we still need to wait for parse/apply
    // to avoid a frame of unstyled dimensions.
    ensureCssLoaded(cssHrefs).then(() => {
      container.style.visibility = 'visible';
    });
  }, [navHtml, cssHrefs, variant]);

  if (!navHtml) return null;

  return <div ref={containerRef} id={`mirrored-left-nav-${variant}`} style={{ visibility: 'hidden' }} />;
};

export default MirroredLeftNav;
